// The composer's half of scheduled messages: «Programar» from the send button (right-click, or a
// held press — MacComposerControls' contextMenu + LongPressGesture(0.45 s)), the strip above the
// box, and the two sheets. Composer wires it with a few lines; the box is cleared only once the
// server ACCEPTED the schedule (ChannelViewModel.scheduleComposedMessage): a composer that empties
// on tap and then fails would have thrown away the only copy.
import { useContext, useRef, useState, type ReactNode } from 'react';
import { data } from '../../../bridge/client';
import { scheduledWhenLabel } from '../../../core/scheduledPost';
import { t, tf } from '../../../i18n/i18n';
import { ShellContext } from '../../shell/ShellContext';
import { ScheduleSheet } from './ScheduleSheet';
import { ScheduledList, ScheduledStrip, useScheduled } from './ScheduledList';

const HOLD_MS = 450;

export interface ComposerSchedule {
  /** The «N mensajes programados · Ver» strip (null when nothing is scheduled). */
  strip: ReactNode;
  /** Whichever sheet is open. */
  sheets: ReactNode;
  /** Spread on the send button: right-click / long-press opens «Programar». */
  sendButtonProps: {
    onContextMenu(e: React.MouseEvent): void;
    onPointerDown(e: React.PointerEvent): void;
    onPointerUp(): void;
    onPointerLeave(): void;
  };
  /** Whether a click on send right now is the tail of a long-press (and must not also send). */
  swallowClick(): boolean;
  open(): void;
}

export function useComposerSchedule(opts: {
  channelId: string;
  rootId: string | null;
  /** Whether there is something to schedule (text or files) and nothing blocks it. */
  canSchedule(): boolean;
  /** What to hand over: the trimmed text and the tray's ids (not taken yet). */
  collect(): { message: string; fileIds: string[] };
  /** The server accepted: empty the box and the tray. */
  onScheduled(): void;
}): ComposerSchedule {
  const shell = useContext(ShellContext);
  const teamId = shell?.team.id ?? '';
  const rows = useScheduled(opts.channelId, opts.rootId);
  const [sheet, setSheet] = useState<'pick' | 'list' | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);

  const open = () => {
    // A team-less surface cannot schedule: the server's list is per team.
    if (!teamId || !opts.canSchedule()) return;
    setSheet('pick');
  };

  const schedule = async (atMillis: number) => {
    const { message, fileIds } = opts.collect();
    const result = await data.call('scheduleMessage', { channelId: opts.channelId, teamId, message, scheduledAt: atMillis, rootId: opts.rootId, fileIds }).catch(() => ({ ok: false as const, reason: 'network' as const }));
    if (result.ok) {
      opts.onScheduled();
      shell?.toast(tf('web.scheduled.scheduled', scheduledWhenLabel(atMillis, Date.now())));
    } else {
      // Nothing is cleared: the words AND the chips are still the only copy.
      shell?.toast(result.reason === 'upload' ? t('web.scheduled.uploadFailed') : t('schedule.error.failed'), 'error');
    }
  };

  const cancelHold = () => {
    if (hold.current) clearTimeout(hold.current);
    hold.current = null;
  };

  return {
    strip: <ScheduledStrip count={rows.length} onOpen={() => setSheet('list')} />,
    sheets:
      sheet === 'pick' ? (
        <ScheduleSheet onClose={() => setSheet(null)} onSchedule={(at) => void schedule(at)} />
      ) : sheet === 'list' ? (
        <ScheduledList rows={rows} onClose={() => setSheet(null)} />
      ) : null,
    sendButtonProps: {
      onContextMenu: (e) => {
        if (!teamId || !opts.canSchedule()) return;
        e.preventDefault();
        open();
      },
      onPointerDown: (e) => {
        if (e.button !== 0) return;
        held.current = false;
        cancelHold();
        hold.current = setTimeout(() => {
          hold.current = null;
          if (!teamId || !opts.canSchedule()) return;
          held.current = true;
          open();
        }, HOLD_MS);
      },
      onPointerUp: cancelHold,
      onPointerLeave: cancelHold,
    },
    swallowClick: () => {
      const was = held.current;
      held.current = false;
      return was;
    },
    open,
  };
}
